import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import AppShell, { SettingsGroup } from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import {
  DEFAULT_VIEWER_ADDRESS,
  DEFAULT_VIEWER_LATITUDE,
  DEFAULT_VIEWER_LONGITUDE,
  loadViewerLocationState,
  lookupIpApproximateLocation,
  requestLocationOnce,
  resolveHomeViewerLocation,
  saveViewerLocationState,
  searchPlaces,
  setFixedViewerLocation,
  ViewerLocationMode,
  ViewerLocationState,
} from '../utils/geolocation';
import { useT } from '../i18n/useT';

const LOCATION_TIMEOUT_MS = 8000;

export default function LocationSettingsPage() {
  const t = useT();
  const [state, setState] = useState<ViewerLocationState>(() => loadViewerLocationState());
  const [effectiveLabel, setEffectiveLabel] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ label: string; lat: string; lng: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchSeq = useRef(0);

  const refreshEffective = async (next?: ViewerLocationState) => {
    const resolved = await resolveHomeViewerLocation({
      state: next ?? loadViewerLocationState(),
      timeoutMs: LOCATION_TIMEOUT_MS,
    });
    setEffectiveLabel(`${resolved.label} (${resolved.source})`);
  };

  useEffect(() => {
    void refreshEffective();
    // Mount-only: show current effective location from stored prefs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.mode !== 'fixed') {
      setResults([]);
      return;
    }
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(() => {
      setSearching(true);
      void searchPlaces(q).then((rows) => {
        if (seq !== searchSeq.current) return;
        setResults(rows);
        setSearching(false);
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [query, state.mode]);

  const persist = (next: ViewerLocationState, note?: string) => {
    saveViewerLocationState(next);
    setState(next);
    setMessage(note ?? null);
    setError(null);
    void refreshEffective(next);
  };

  const onModeChange = async (mode: ViewerLocationMode) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (mode === 'gps') {
        const coords = await requestLocationOnce(navigator.geolocation, LOCATION_TIMEOUT_MS);
        if (!coords) {
          persist(
            { ...state, mode: 'gps' },
            t('locationGpsUnavailable')
          );
          return;
        }
        persist(
          {
            mode: 'gps',
            address: t('locationCurrentAddress'),
            lat: coords.lat,
            lng: coords.lng,
          },
          t('locationUsingGps')
        );
        return;
      }

      if (mode === 'ip') {
        const approx = await lookupIpApproximateLocation();
        if (!approx) {
          setError(t('locationIpFailed'));
          persist({ ...state, mode: 'ip' });
          return;
        }
        persist(
          {
            mode: 'ip',
            address: approx.label,
            lat: approx.lat,
            lng: approx.lng,
          },
          t('locationUsingIp')
        );
        return;
      }

      persist(
        {
          ...state,
          mode: 'fixed',
          address: state.address || DEFAULT_VIEWER_ADDRESS,
        },
        t('locationPickFixed')
      );
    } finally {
      setBusy(false);
    }
  };

  const onPickPlace = (place: { label: string; lat: string; lng: string }) => {
    setFixedViewerLocation(place.label, place.lat, place.lng);
    const next = loadViewerLocationState();
    setState(next);
    setQuery(place.label);
    setResults([]);
    setMessage(t('locationFixedSaved'));
    setError(null);
    void refreshEffective(next);
  };

  const onUseZurich = () => {
    setFixedViewerLocation(
      DEFAULT_VIEWER_ADDRESS,
      String(DEFAULT_VIEWER_LATITUDE),
      String(DEFAULT_VIEWER_LONGITUDE)
    );
    const next = loadViewerLocationState();
    setState(next);
    setQuery(DEFAULT_VIEWER_ADDRESS);
    setMessage(t('locationZurichDefault'));
    void refreshEffective(next);
  };

  return (
    <AppShell title={t('locationSettingsTitle')} showBackButton maxWidth="sm" showCompanion={false}>
      <Typography sx={{ mb: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
        {t('locationSettingsIntro')}
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SettingsGroup>
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <FormControl disabled={busy} fullWidth>
            <RadioGroup
              value={state.mode}
              onChange={(e) => void onModeChange(e.target.value as ViewerLocationMode)}
            >
              <FormControlLabel
                value="gps"
                control={<Radio />}
                label={t('locationModeGps')}
              />
              <FormControlLabel
                value="ip"
                control={<Radio />}
                label={t('locationModeIp')}
              />
              <FormControlLabel
                value="fixed"
                control={<Radio />}
                label={t('locationModeFixed')}
              />
            </RadioGroup>
          </FormControl>
          {busy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary }}>
                {t('locationUpdating')}
              </Typography>
            </Box>
          )}
        </Box>
      </SettingsGroup>

      {state.mode === 'fixed' && (
        <SettingsGroup>
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label={t('locationSearchLabel')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('locationSearchPlaceholder')}
            />
            <Button sx={{ mt: 1.5 }} size="small" onClick={onUseZurich}>
              {t('locationUseZurich')}
            </Button>
            {searching && (
              <Typography sx={{ mt: 1, fontSize: 13, color: DesignSystem.colors.textSecondary }}>
                {t('locationSearching')}
              </Typography>
            )}
            {results.length > 0 && (
              <List dense sx={{ mt: 1, maxHeight: 280, overflow: 'auto' }}>
                {results.map((row) => (
                  <ListItemButton key={`${row.lat},${row.lng},${row.label}`} onClick={() => onPickPlace(row)}>
                    <ListItemText
                      primary={row.label}
                      primaryTypographyProps={{ fontSize: 14 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </SettingsGroup>
      )}

      <Typography sx={{ mt: 2, fontSize: 13, color: DesignSystem.colors.textSecondary }}>
        {t('locationActiveForHome', { label: effectiveLabel || '…' })}
      </Typography>
      {state.mode === 'fixed' && (
        <Typography sx={{ mt: 0.5, fontSize: 13, color: DesignSystem.colors.textSecondary }}>
          {t('locationSaved', { address: state.address })}
        </Typography>
      )}
    </AppShell>
  );
}
